/**
 * Renders a Contentful `sectionLayout1` section of a CMS page: an optional heading and plain-text
 * paragraphs in one half, an image in the other.
 *
 * Rendered by `ContentfulPage.tsx` for each section whose `content_type` is `sectionLayout1`. The
 * fields it reads must stay in `ContentfulSectionLayout1Fields`, which `ContentfulUtils.ts` →
 * `getPageContent` passes to `Crawl`.
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { ContentSection } from "@ryerson/frontend.layout";
import { Grid, Row, Column } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { useApplication } from "@ryerson/frontend.application";
import { IContentfulBaseFields } from "./ContentfulPage";
import ContentfulUtils from "./ContentfulUtils";

/** A `sectionLayout1` entry: text beside an image. */
export interface IContentfulSectionLayout1 extends IContentfulBaseFields {
	// The entry's name in Contentful; not rendered.
	name: string;
	// The colour scheme for `ContentSection` and `Typography`; lower-cased by
	// `ContentfulUtils.getType`.
	type: "primary" | "secondary" | "tertiary";
	// Which side of the text the image is on.
	imageSide: "Left" | "Right";
	title?: string;
	// Plain text; each line becomes a paragraph. Required: the component splits it unchecked.
	description: string;
	// A Contentful asset. `file.url` is protocol-relative (`//images.ctfassets.net/...`), so the
	// component prefixes `https:`. The alt text is `title - description`.
	image: {
		title: string;
		description: string;
		file: {
			url: string;
		};
	};
}

/**
 * The fields `getPageContent` keeps for this section, as dot paths under the page's `sections`
 * field.
 */
export const ContentfulSectionLayout1Fields = [
	"name",
	"type",
	"imageSide",
	"title",
	"description",
	"image",
	"image.title",
	"image.description",
	"image.file.url",
].map((field) => `sections.${field}`);

/** Props for `ContentfulSectionLayout1`: one `sectionLayout1` entry from a page's `sections`. */
export interface ContentfulSectionLayout1Props {
	content: IContentfulSectionLayout1;
}

/** Renders one `sectionLayout1` section; see the file header. */
const ContentfulSectionLayout1: React.FC<ContentfulSectionLayout1Props> = ({ content }) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	const type = ContentfulUtils.getType(content.type);

	return (
		<ContentSection type={type} vPadding="40px">
			<Grid gutter={20}>
				{/* Text comes first in the markup; `reverse` puts the image on the left. */}
				<Row reverse={content.imageSide === "Left" ? true : false}>
					<Column md={6}>
						{content.title && (
							<Typography type={type} variant="h2">
								{content.title}
							</Typography>
						)}
						{content.description.split("\n").map((paragraph, index) => (
							<Typography key={index} type={type} variant="p">
								{paragraph}
							</Typography>
						))}
					</Column>
					<Column md={6}>
						<img
							src={"https:" + content.image.file.url}
							alt={content.image.title + " - " + content.image.description}
							width="100%"
							height="100%"
							style={{ objectFit: "cover", borderRadius: "20px" }}
						/>
					</Column>
				</Row>
			</Grid>
		</ContentSection>
	);
};

export default ContentfulSectionLayout1;
